import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Spinner,
  Row,
  Col,
} from "reactstrap";
import * as XLSX from "xlsx";
import CustomerServices from "../../../services/OrderingServices/CustomerServices";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import TableSearch from "../../../Components/Common/TableSearch";
import useTableSearch from "../../../hooks/useTableSearch";

const CustomerBulkUploadPage = () => {
  const [excelData, setExcelData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // For S.N calculation if needed, though pagination handles display
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const navigate = useNavigate();

  // Search hook for local filtering
  const { searchTerm, setSearchTerm, filteredData } = useTableSearch(
    excelData,
    ["firstName", "lastName", "email", "phoneNo", "address"],
  );

  // Helper: Find value in row regardless of typos (mapping to Customer fields)
  const getVal = (row, possibleNames) => {
    const rowKeys = Object.keys(row);
    for (let name of possibleNames) {
      const match = rowKeys.find(
        (k) => k.trim().toLowerCase() === name.toLowerCase(),
      );
      if (match !== undefined) return row[match];
    }
    return "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setLoading(true);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const workbook = XLSX.read(bstr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const rawData = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
        });

        if (rawData.length === 0) {
          toast.error("No data found in Excel file.");
          setLoading(false);
          return;
        }

        const formattedData = rawData.map((row) => ({
          firstName:
            getVal(row, ["FirstName", "First Name", "Name"])
              ?.toString()
              .trim() || "",
          lastName:
            getVal(row, ["LastName", "Last Name"])?.toString().trim() || "",
          email:
            getVal(row, ["Email", "Email Address"])?.toString().trim() || "",
          phoneNo:
            getVal(row, ["PhoneNo", "Phone No", "Mobile", "Phone", "Contact"])
              ?.toString()
              .trim() || "",
          address: getVal(row, ["Address"])?.toString().trim() || "",
          customerType : getVal(row, ["CustomerType", "Customer Type"])?.toString().trim() || "",
        }));

        // Filter out empty rows (at least FirstName or PhoneNo should exist)
        const validData = formattedData.filter(
          (item) => item.firstName || item.phoneNo,
        );
        setExcelData(validData);

        toast.success(`${validData.length} records processed.`);
      } catch (error) {
        console.error(error);
        toast.error("Failed to parse file.");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const downloadCustomerTemplate = () => {
    const templateData = [
      {
        FirstName: "John",
        LastName: "Doe",
        Email: "john@example.com",
        PhoneNo: "9800000001",
        Address: "Kathmandu",
        CustomerType: "Family",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Template");

    const colWidths = [
      { wch: 20 }, // FirstName
      { wch: 20 }, // LastName
      { wch: 30 }, // Email
      { wch: 15 }, // PhoneNo
      { wch: 30 }, // Address
      { wch: 15 }, // CustomerTypeId
    ];

    worksheet["!cols"] = colWidths;
    XLSX.writeFile(workbook, "customer_import_template.xlsx");
  };

  const handleFinalSubmit = async () => {
    if (!selectedFile) {
      toast.error("No file selected.");
      return;
    }
    setSubmitting(true);
    try {
      await CustomerServices.bulkImportExcel(selectedFile);
      toast.success("All records saved successfully!");
      setExcelData([]);
      setSelectedFile(null);
      setTimeout(() => {
        navigate("/customer-list");
      }, 1500);
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "Error saving to backend.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "80px",
      center: true,
    },
    {
      name: "First Name",
      selector: (row) => row.firstName,
      sortable: true,
    },
    {
      name: "Last Name",
      selector: (row) => row.lastName,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phoneNo,
      sortable: true,
    },
    {
      name: "Address",
      selector: (row) => row.address,
      sortable: true,
    },
    {
      name: "Customer Types",
      selector: (row) => row.customerTypeId,
      // width: "100px",
      center: true,
    },
  ];

  const breadcrumbItems = [{ title: "Back To List", link: "/customer-list" }];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Customer Bulk Import"
          pageTitle="Customer Management"
          breadcrumbItems={breadcrumbItems}
        />

        <Card className="shadow-sm border-0">
          <CardHeader className="bg-white border-bottom py-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <h5 className="mb-0 fw-bold text-primary">
                <i className="ri-file-excel-2-line me-2"></i> Preview Records
              </h5>

              <div className="d-flex gap-2 align-items-center flex-wrap">
                <Button
                  color="light"
                  className="btn-sm border d-flex align-items-center gap-1 shadow-sm"
                  onClick={downloadCustomerTemplate}
                >
                  <i className="ri-download-line"></i> Download Template
                </Button>

                <input
                  type="file"
                  id="fileInput"
                  hidden
                  onChange={handleFileChange}
                  accept=".xlsx, .xls"
                />
                <Button
                  color="info"
                  outline
                  className="btn-sm d-flex align-items-center gap-1 shadow-sm"
                  onClick={() => document.getElementById("fileInput").click()}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-folder-open-line"></i> Choose File
                    </>
                  )}
                </Button>

                {excelData.length > 0 && (
                  <Button
                    color="success"
                    className="btn-sm d-flex align-items-center gap-1 shadow-sm"
                    onClick={handleFinalSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <i className="ri-save-line"></i> Save All Records
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardBody>
            <Row className="mb-3">
              <Col md={4}>
                <TableSearch
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Filter preview data..."
                />
              </Col>
            </Row>

            <div className="table-responsive">
              <CustomDataTable
                columns={columns}
                data={filteredData}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50]}
                highlightOnHover
                striped
                progressPending={loading || submitting}
                noDataComponent={
                  <div className="text-center my-5">
                    <i className="ri-file-excel-line display-4 text-muted"></i>
                    <p className="mt-2 text-muted">
                      {excelData.length > 0
                        ? "No results match your search."
                        : "Upload an Excel file to see the data preview here."}
                    </p>
                  </div>
                }
              />
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default CustomerBulkUploadPage;
